import React from 'react';
import { Node, Edge, useReactFlow } from '@xyflow/react';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'store/store';
import { RuleSearchType } from '../../rules.types';
import { rulesApi } from '../../rulesApi';
import logger from 'lib/logger';
import { AutomationSearchFormValues } from '../automations.types';
import { getGraphFromPerkRules, getGraphFromAudienceRules } from '../utils/graphBuilders';
import { getLayoutedElements, elkOptions } from '../utils/layoutCalculator';

export function useAutomationSearch(onUrlUpdate: (params: Record<string, any>) => void) {
    const { perkIdToTitle } = useSelector((state: ApplicationState) => state.perks);
    const { audiences } = useSelector((state: ApplicationState) => state.audiences);
    const [isRulesLoading, setIsRulesLoading] = React.useState(false);
    const [nodes, setNodes] = React.useState<Node[]>([]);
    const [edges, setEdges] = React.useState<Edge[]>([]);

    const { fitView } = useReactFlow();

    const handleSubmitSearch = React.useCallback(
        async (values: AutomationSearchFormValues) => {
            setIsRulesLoading(true);
            onUrlUpdate({ type: values.type, search: values.search });
            const result = await rulesApi.searchRules(values.type, values.search);
            setIsRulesLoading(false);
            if (!result.ok) {
                return logger.error('Failed to search rules', result.body.message);
            }
            let [ns, es]: [Node[], Edge[]] = [[], []];
            if (values.type === RuleSearchType.Perk) {
                const coreTitle = perkIdToTitle.get(values.search) ?? values.search;
                [ns, es] = getGraphFromPerkRules(
                    result.body.matchedEffects,
                    result.body.matchedConditions,
                    values.search,
                    `"${coreTitle}" perk`
                );
            }
            if (values.type === RuleSearchType.Audience) {
                const coreTitle =
                    audiences.find(audience => audience._id === values.search)?.title ?? values.search;
                [ns, es] = getGraphFromAudienceRules(
                    result.body.matchedEffects,
                    result.body.matchedConditions,
                    values.search,
                    `"${coreTitle}" audience`
                );
            }
            getLayoutedElements(ns, es, elkOptions).then((value: any) => {
                const { nodes: layoutedNodes, edges: layoutedEdges } = value;
                setNodes(layoutedNodes);
                setEdges(layoutedEdges);
                const flag = true;
                if (flag) {
                    fitView();
                }
            });
            return null;
        },
        [audiences, fitView, perkIdToTitle, onUrlUpdate]
    );

    return {
        handleSubmitSearch,
        isRulesLoading,
        nodes,
        edges
    };
}
